import {
  type CreateAdminAgencyDto,
  type UpdateAdminAgencyDto,
  type V2AdminAgenciesPageDto,
  type V2AdminAgenciesSummaryDto,
  type V2AdminAgencyDto,
  v2AdminAgenciesControllerCreate,
  v2AdminAgenciesControllerDetail,
  v2AdminAgenciesControllerList,
  v2AdminAgenciesControllerLookup,
  v2AdminAgenciesControllerRemove,
  v2AdminAgenciesControllerSummary,
  v2AdminAgenciesControllerUpdate
} from '@idc-forms/api-client'
import {
  V2AdminAgenciesPageSchema,
  V2AdminAgenciesSummarySchema,
  V2AdminAgencyLookupSchema,
  V2AdminAgencySchema
} from '@idc-forms/schemas/api-v2/admin-agencies'
import { assertShape } from '@/shared/api/assert-shape'
import { bearer, v2RequestOptions } from '@/shared/api/v2-request'

/**
 * Administración de agencias contra la API v2 (ADR 0019/0020). Consume el SDK
 * generado con el Bearer de la sesión staff v2, pagina por cursor y usa el
 * contrato de consulta rico. Identifica todo por id público (`agc_`); la empresa
 * madre viaja por `cmp_` y la autoría por `usr_`, nunca como ObjectId.
 *
 * `v2RequestOptions` es OBLIGATORIO en cada llamada: trae el `querySerializer`
 * con bracket notation. El serializer por defecto del SDK usa `deepObject` de UN
 * solo nivel y explota en runtime ante cualquier filtro anidado del ADR 0020.
 */

/** Parámetros amigables del listado; el servicio los mapea al query del SDK. */
/** Referencia de agencia para selects v2: `agc_` + nombre + su empresa (`cmp_`). */
export type AgencyV2LookupOption = {
  id: string
  name: string
  company: string
  default: boolean
}

export type AgenciesAdminV2Query = {
  filters?: Record<string, unknown>
  /** `campo:asc|desc` (un solo campo; el cursor desempata por id). */
  sort?: string
  search?: string
  pageSize?: number
  after?: string
  before?: string
}

export type AgenciesAdminV2Row = V2AdminAgenciesPageDto['data'][number]

function toListQuery(query: AgenciesAdminV2Query) {
  return {
    ...(query.filters && Object.keys(query.filters).length ? { filters: query.filters } : {}),
    ...(query.sort ? { sort: query.sort } : {}),
    ...(query.search ? { search: query.search } : {}),
    ...(query.pageSize ? { 'page[size]': query.pageSize } : {}),
    ...(query.after ? { 'page[after]': query.after } : {}),
    ...(query.before ? { 'page[before]': query.before } : {})
  }
}

export const agenciesAdminV2Service = {
  list: async (
    accessToken: string,
    query: AgenciesAdminV2Query
  ): Promise<V2AdminAgenciesPageDto> => {
    const result = await v2AdminAgenciesControllerList({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminAgenciesPageSchema, result.data)
  },

  summary: async (
    accessToken: string,
    query: Pick<AgenciesAdminV2Query, 'filters' | 'search'>
  ): Promise<V2AdminAgenciesSummaryDto> => {
    const result = await v2AdminAgenciesControllerSummary({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      query: toListQuery(query)
    })
    return assertShape(V2AdminAgenciesSummarySchema, result.data)
  },

  detail: async (accessToken: string, id: string): Promise<V2AdminAgencyDto> => {
    const result = await v2AdminAgenciesControllerDetail({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
    return assertShape(V2AdminAgencySchema, result.data)
  },

  create: async (accessToken: string, body: CreateAdminAgencyDto): Promise<V2AdminAgencyDto> => {
    const result = await v2AdminAgenciesControllerCreate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      body
    })
    return result.data
  },

  update: async (
    accessToken: string,
    id: string,
    body: UpdateAdminAgencyDto
  ): Promise<V2AdminAgencyDto> => {
    const result = await v2AdminAgenciesControllerUpdate({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id },
      body
    })
    return result.data
  },

  remove: async (accessToken: string, id: string): Promise<void> => {
    await v2AdminAgenciesControllerRemove({
      ...v2RequestOptions,
      headers: bearer(accessToken),
      path: { id }
    })
  },

  /**
   * Selector de agencias. Cada una viaja con su empresa (`cmp_`), que es lo que
   * necesita la cascada empresa→agencia de la ficha del cliente: sin eso, la
   * pantalla tendría que cruzar dos listados para saber qué agencia ofrecer.
   */
  lookup: async (accessToken: string): Promise<AgencyV2LookupOption[]> => {
    const result = await v2AdminAgenciesControllerLookup({
      ...v2RequestOptions,
      headers: bearer(accessToken)
    })
    return assertShape(V2AdminAgencyLookupSchema, result.data).data.map((agency) => ({
      id: agency.id,
      name: agency.name,
      company: agency.company,
      default: agency.default
    }))
  }
}
